import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Circle } from 'lucide-react';

import { useSeasonStore } from '@/stores/season-store';
import { usePropertyStore } from '@/stores/property-store';
import { useWaypointStore } from '@/stores/waypoint-store';
import { useAuthStore } from '@/stores/auth-store';
import { Button } from '@/components/common/Button';
import { TypePickerSheet } from '@/components/waypoint/TypePickerSheet';
import type { WaypointType } from '@/types/waypoint';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type StepKey = 'account' | 'season' | 'property' | 'location';
type StepStatus = 'done' | 'active' | 'pending';

interface SetupStep {
  key: StepKey;
  label: string;
  ctaLabel: string;
  getRoute: (properties: { id: string }[]) => string;
  isComplete: boolean;
}

const BASE_DISMISS_KEY = 'ridgen:getting-started-dismissed';

/** Returns a userId-scoped dismiss key so the flag persists per account across
 *  re-logins on the same device. Falls back to the base key when userId is
 *  unavailable (unauthenticated or tests that don't mock useAuthStore). */
function dismissKey(userId: string | null | undefined): string {
  return userId ? `${BASE_DISMISS_KEY}:${userId}` : BASE_DISMISS_KEY;
}

// ---------------------------------------------------------------------------
// SetupMissionCard
// ---------------------------------------------------------------------------

export function SetupMissionCard() {
  // RULE-024: all hooks before any conditional returns
  const { user } = useAuthStore();
  const userId = user?.id ?? null;
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(dismissKey(userId)) === 'true',
  );

  // Re-check dismiss flag when userId becomes available (auth loads after mount)
  useEffect(() => {
    if (userId && localStorage.getItem(dismissKey(userId)) === 'true') {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- one-shot sync from external prop on mount
      setDismissed(true);
    }
  }, [userId]);

  const navigate = useNavigate();
  const { seasons } = useSeasonStore();
  const { properties } = usePropertyStore();
  const { waypoints } = useWaypointStore();
  const [typePickerOpen, setTypePickerOpen] = useState(false);

  const steps: SetupStep[] = [
    {
      key: 'account',
      label: 'Create account',
      ctaLabel: '',
      getRoute: () => '/',
      isComplete: true, // always true — user is authenticated
    },
    {
      key: 'season',
      label: 'Define your season',
      ctaLabel: 'Define Your Season',
      getRoute: () => '/seasons/new',
      isComplete: seasons.length > 0,
    },
    {
      key: 'property',
      label: 'Map your property',
      ctaLabel: 'Map Property',
      getRoute: () => '/land/properties/new',
      isComplete: properties.length > 0,
    },
    {
      key: 'location',
      label: 'Add your waypoints',
      ctaLabel: 'Add Waypoint',
      // No-property fallback only — when a property exists, the location step's
      // CTA opens the TypePickerSheet instead of navigating directly (see
      // handleCta below), so this route is only used when properties is empty.
      getRoute: (props) =>
        props.length > 0
          ? ''
          : '/land/properties',
      isComplete: waypoints.length > 0,
    },
  ];

  const allDone = steps.slice(1).every((s) => s.isComplete);

  // State 1: dismissed — render nothing
  if (dismissed) return null;

  function handleDismiss() {
    localStorage.setItem(dismissKey(userId), 'true');
    setDismissed(true);
  }

  // State 2: all done + not dismissed — completion state
  if (allDone) {
    return (
      <div
        className="bg-bark-800 border-[0.5px] border-bark-600 rounded-lg shadow-card p-4 flex flex-col gap-3"
        data-testid="setup-mission-card"
        data-card-state="complete"
      >
        {/* Header */}
        <p className="field-label">GETTING STARTED</p>

        {/* All 4 steps shown as done */}
        <div className="flex flex-col gap-0">
          {steps.map((step) => (
            <StepRow
              key={step.key}
              step={step}
              status="done"
              onCta={() => { }}
            />
          ))}
        </div>

        {/* Completion message */}
        <p className="text-body-sm text-[var(--color-text-muted)]">
          You&apos;re set up. Your land is mapped, your season is defined, and your first waypoint is
          pinned. Time to hunt.
        </p>

        {/* Dismiss CTA */}
        <Button
          variant="tactical"
          data-testid="setup-mission-dismiss"
          onClick={handleDismiss}
        >
          Start Exploring
        </Button>
      </div>
    );
  }

  // State 3: incomplete — existing behavior
  let activeAssigned = false;
  const stepStatuses: StepStatus[] = steps.map((step) => {
    if (step.isComplete) return 'done';
    if (!activeAssigned) {
      activeAssigned = true;
      return 'active';
    }
    return 'pending';
  });

  function handleCta(step: SetupStep) {
    // The location step opens the waypoint TypePickerSheet instead of
    // navigating directly when a property exists (mirrors MapPage.handleTypeSelected).
    if (step.key === 'location' && properties.length > 0) {
      setTypePickerOpen(true);
      return;
    }
    navigate(step.getRoute(properties), { state: { fromOnboarding: true } });
  }

  function handleTypeSelected(type: WaypointType) {
    setTypePickerOpen(false);
    navigate(
      `/land/properties/${properties[0].id}/waypoints/new?type=${type}`,
      { state: { fromOnboarding: true } },
    );
  }

  return (
    <div
      className="bg-bark-800 border-[0.5px] border-bark-600 rounded-lg shadow-card p-4 flex flex-col gap-3"
      data-testid="setup-mission-card"
    >
      {/* Header */}
      <p className="field-label">GETTING STARTED</p>

      {/* Steps */}
      <div className="flex flex-col gap-0">
        {steps.map((step, idx) => {
          const status = stepStatuses[idx];
          return (
            <StepRow
              key={step.key}
              step={step}
              status={status}
              onCta={() => handleCta(step)}
            />
          );
        })}
      </div>

      {/* Waypoint type picker — opens for the location step CTA when a property exists */}
      <TypePickerSheet
        isOpen={typePickerOpen}
        onTypeSelected={handleTypeSelected}
        onClose={() => setTypePickerOpen(false)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// StepRow
// ---------------------------------------------------------------------------

interface StepRowProps {
  step: SetupStep;
  status: StepStatus;
  onCta: () => void;
}

function StepRow({ step, status, onCta }: StepRowProps) {
  const borderColor =
    status === 'done'
      ? 'border-l-[var(--color-done)]'
      : status === 'active'
        ? 'border-l-[var(--color-accent)]'
        : 'border-l-[var(--color-border)]';

  const textColor =
    status === 'done' || status === 'pending'
      ? 'text-[var(--color-text-muted)]'
      : 'text-[var(--color-text-primary)]';

  return (
    <div
      className={`flex items-center gap-3 pl-3 pr-2 py-2.5 border-l-2 ${borderColor}`}
      data-setup-step={step.key}
      data-setup-status={status}
    >
      {/* Icon */}
      <span className="shrink-0">
        {status === 'done' ? (
          <Check size={14} className="text-[var(--color-done)]" aria-hidden="true" />
        ) : (
          <Circle
            size={14}
            className={status === 'active' ? 'text-[var(--color-accent)]' : 'text-bark-400'}
            aria-hidden="true"
          />
        )}
      </span>

      {/* Label */}
      <span className={`flex-1 text-body-sm ${textColor}`}>{step.label}</span>

      {/* CTA — only on active step */}
      {status === 'active' && step.ctaLabel && (
        <Button
          type="button"
          variant="primary"
          onClick={onCta}
          className="px-3 py-1.5 text-body-xs"
          data-testid={`setup-cta-${step.key}`}
        >
          {step.ctaLabel}
        </Button>
      )}
    </div>
  );
}
